import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { cycleSuffix, propertyTypeSummary, type RentCycle } from "@rentorng/core";
import { PropertyTypeIcon, isResidentialType } from "@/components/property-icons";
import { api } from "@/lib/api";
import { useApiMutation } from "@/lib/mutations";
import { Alert, Button, Card, EmptyState, ErrorState, Field, Input, Spinner, Textarea } from "@/components/ui";
import { PhoneInput } from "@/components/phone-input";
import { Copyright } from "@/components/copyright";
import { usePageTitle } from "@/lib/title";

/**
 * An agency's public vacancy page.
 *
 * Not a marketplace and not a portal — it exists to fill *this agency's* own units. The enquiry
 * form writes a lead into their manager app, which is the only reason the page earns its keep.
 */

interface ListingsResponse {
  agency: { name: string; phone: string | null; email: string | null; currency: string };
  listings: Array<{
    id: string;
    label: string;
    bedrooms: number | null;
    bathrooms: number | null;
    sizeSqm: number | null;
    rentCycle: string;
    description: string | null;
    propertyName: string;
    city: string | null;
    state: string | null;
    types: string[];
    formattedRent: string | null;
  }>;
}

export function ListingsPage() {
  const { slug } = useParams();
  const [enquiringAbout, setEnquiringAbout] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["listings", slug],
    queryFn: () => api.get<ListingsResponse>(`/public/listings/${slug}`),
    enabled: Boolean(slug),
    retry: false,
  });

  const enquire = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.post(`/public/enquiries/${slug}`, input),
    onSuccess: () => setEnquiringAbout(null),
  });

  usePageTitle(data?.agency.name ? `Available to let · ${data.agency.name}` : "Available to let");

  return (
    <div className="min-h-screen bg-ink-50">
      <header className="border-b border-ink-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-4">
          <div className="flex items-center gap-2.5">
            <img src="/logo.svg" alt="" width={28} height={28} className="rounded-lg" />
            <span className="font-semibold tracking-tight text-brand-900">{data?.agency.name ?? "Available to let"}</span>
          </div>
          {data?.agency.phone ? <a href={`tel:${data.agency.phone}`} className="text-sm text-ink-600 hover:underline">{data.agency.phone}</a> : null}
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-8">
        {isLoading ? (
          <Spinner />
        ) : error || !data ? (
          <Card>
            <ErrorState error={error} retry={() => refetch()} />
          </Card>
        ) : (
          <>
            <h1 className="text-2xl font-semibold">Available to let</h1>
            <p className="mt-1 text-ink-600">
              {data.listings.length} {data.listings.length === 1 ? "property" : "properties"} from {data.agency.name}.
            </p>

            {enquire.isSuccess ? (
              <div className="mt-4">
                <Alert tone="success" title="Thank you.">
                  Your enquiry has been sent. Someone will be in touch.
                </Alert>
              </div>
            ) : null}

            {data.listings.length === 0 ? (
              <Card className="mt-6">
                <EmptyState title="Nothing is available right now." hint="Check back, or call the number above." />
              </Card>
            ) : (
              <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {data.listings.map((listing) => (
                  <Card key={listing.id} className="flex flex-col">
                    <p className="flex items-center gap-2 text-xs uppercase tracking-wide text-ink-400">
                      <PropertyTypeIcon
                        type={(listing.types ?? [])[0] ?? "block_of_flats"}
                        size={16}
                        className={
                          isResidentialType((listing.types ?? [])[0] ?? "") ? "text-brand-600" : "text-info-600"
                        }
                      />
                      {propertyTypeSummary(listing.types ?? [])}
                    </p>
                    <h2 className="mt-1 text-base font-semibold">
                      {listing.label} · {listing.propertyName}
                    </h2>
                    <p className="text-sm text-ink-500">{[listing.city, listing.state].filter(Boolean).join(", ")}</p>

                    <p className="tabular mt-3 text-lg font-semibold text-brand-800">
                      {listing.formattedRent ?? "On application"}
                      {listing.formattedRent ? (
                        <span className="ml-1 text-xs font-normal text-ink-500">{cycleSuffix(listing.rentCycle as RentCycle)}</span>
                      ) : null}
                    </p>

                    <p className="mt-1 text-sm text-ink-600">
                      {[
                        listing.bedrooms ? `${listing.bedrooms} bed` : null,
                        listing.bathrooms ? `${listing.bathrooms} bath` : null,
                        listing.sizeSqm ? `${listing.sizeSqm} sqm` : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>

                    {listing.description ? <p className="mt-2 flex-1 text-sm text-ink-600">{listing.description}</p> : <div className="flex-1" />}

                    <Button
                      variant="secondary"
                      className="mt-4"
                      onClick={() => setEnquiringAbout(enquiringAbout === listing.id ? null : listing.id)}
                    >
                      {enquiringAbout === listing.id ? "Close" : "Enquire"}
                    </Button>

                    {enquiringAbout === listing.id ? (
                      <form
                        className="mt-3 space-y-3 border-t border-ink-100 pt-3"
                        onSubmit={(event) => {
                          event.preventDefault();
                          const form = new FormData(event.currentTarget);
                          enquire.mutate({
                            unitId: listing.id,
                            name: String(form.get("name")),
                            phone: String(form.get("phone") || "") || null,
                            email: String(form.get("email") || "") || null,
                            message: String(form.get("message") || "") || null,
                          });
                        }}
                      >
                        {enquire.errorMessage ? <Alert tone="danger">{enquire.errorMessage}</Alert> : null}
                        <Field label="Your name" htmlFor={`name-${listing.id}`} required>
                          <Input id={`name-${listing.id}`} name="name" required placeholder="Your name" />
                        </Field>
                        <Field label="Phone" htmlFor={`phone-${listing.id}`}>
                          <PhoneInput id={`phone-${listing.id}`} name="phone" />
                        </Field>
                        <Field label="Email" htmlFor={`email-${listing.id}`}>
                          <Input id={`email-${listing.id}`} name="email" type="email" placeholder="you@example.com" />
                        </Field>
                        <Field label="Message" htmlFor={`message-${listing.id}`}>
                          <Textarea id={`message-${listing.id}`} name="message" rows={2} placeholder="When could I come and see it?" />
                        </Field>
                        <Button type="submit" size="sm" disabled={enquire.isPending}>
                          {enquire.isPending ? "Sending…" : "Send enquiry"}
                        </Button>
                      </form>
                    ) : null}
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        <p className="mt-10 text-center text-xs text-ink-400">
          Listed with{" "}
          <Link to="/" className="hover:underline">
            RentorNG
          </Link>
        </p>
        <Copyright className="mt-2 text-center" />
      </main>
    </div>
  );
}
